import Keycloak from 'keycloak-js'

import { IdentityProfile, IdentityProvider } from './identity-provider.interface'

export interface KeycloakIdentityConfig {
  url: string
  realm: string
  clientId: string
  // When true, app owns the session — disables Keycloak's iframe monitor,
  // silent SSO, and auth-logout reaction.
  appSessionsEnabled?: boolean
}

/** keycloak-js adapter. With appSessionsEnabled, Keycloak is only used for initial login. */
export class KeycloakIdentityProvider implements IdentityProvider {
  readonly providerType = 'keycloak' as const
  private kc: Keycloak
  private readonly appSessionsEnabled: boolean

  constructor(config: KeycloakIdentityConfig) {
    this.kc = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId
    })
    this.appSessionsEnabled = Boolean(config.appSessionsEnabled)
  }

  /** Exposed for legacy callers reading authService.keycloak.profile. */
  get raw(): Keycloak {
    return this.kc
  }

  async init(): Promise<boolean> {
    // App-sessions ON: omit onLoad — keycloak-js only consumes an OIDC callback
    // in the URL, never re-authenticates silently. OFF: legacy check-sso + iframe.
    const initOptions = this.appSessionsEnabled
      ? { checkLoginIframe: false }
      : { onLoad: 'check-sso' as const, checkLoginIframe: true }
    const ok = await this.kc.init(initOptions)
    return Boolean(ok && this.kc.authenticated)
  }

  isAuthenticated(): boolean {
    return Boolean(this.kc.authenticated)
  }

  isTokenExpired(): boolean {
    return this.kc.isTokenExpired ? this.kc.isTokenExpired() : true
  }

  async login(options?: { promptCredentials?: boolean }): Promise<void> {
    await this.kc.login(options?.promptCredentials ? { prompt: 'login' } : undefined)
  }

  async logout(redirectUri: string): Promise<void> {
    await this.kc.logout({ redirectUri })
  }

  getToken(): string | undefined {
    return this.kc.token
  }

  async refreshToken(minValiditySeconds: number): Promise<boolean> {
    return this.kc.updateToken(minValiditySeconds)
  }

  getProfile(): IdentityProfile | undefined {
    if (!this.kc.userInfo && !this.kc.profile) return undefined
    const userInfo = (this.kc.userInfo ?? {}) as Record<string, unknown>
    const profile = (this.kc.profile ?? {}) as Record<string, unknown>
    const realmAccess = userInfo['realm_access'] as { roles?: string[] } | undefined
    return {
      id: (userInfo['sub'] as string) ?? '',
      username: (profile['username'] as string) ?? (userInfo['preferred_username'] as string),
      email: (profile['email'] as string) ?? (userInfo['email'] as string),
      fullName: (userInfo['name'] as string) ?? [profile['firstName'], profile['lastName']].filter(Boolean).join(' '),
      roles: realmAccess?.roles ?? []
    }
  }

  onTokenExpired(cb: () => void): void {
    this.kc.onTokenExpired = cb
  }

  onAuthLogout(cb: () => void): void {
    // When the app owns the session, ignore Keycloak's auth-logout signal;
    // it would otherwise force a re-login redirect when the upstream
    // Keycloak session is killed independently of our app-session.
    if (this.appSessionsEnabled) return
    this.kc.onAuthLogout = cb
  }
}
